import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { Chip, Select } from '@st-operations/uikit/src/v2/index.js'

import './styles.scss'

const ENTER_KEY_CODE = 13
const BACKSCAPE_KEY_CODE = 8
const DELETE_KEY_CODE = 46

const MultiValueInput = ({
  className,
  disabled,
  values = [],
  name,
  label,
  onChange,
  selected,
  onSelect,
  options,
  useFixedMenuPosition = true,
  pasteRegex,
}) => {
  const [value, setValue] = useState('')
  const [inputValue, setInputValue] = useState('')

  const handleValueChange = (event) => {
    if (disabled) return
    if (options) {
      setValue(event.value)
      handleFormatValues(event, event.value)
    } else {
      setValue(event.target.value)
    }
  }

  const handleInputChange = (value) => {
    if (disabled) return
    setInputValue(value)
  }

  const handlePaste = (e) => {
    if (disabled) return
    e.preventDefault()
    const rawText = e.clipboardData.getData('text/plain')
    const pastedValues = rawText.split(pasteRegex)
    onChange([...values, ...pastedValues])
  }

  const handleFormatValues = (event, value) => {
    if (disabled) return
    event?.preventDefault?.()

    // Separate by commas
    const splittedValue = value.split(',')

    onChange([...values, ...splittedValue.map((val) => val.trim())].filter(Boolean), name)
    setValue('')
    setInputValue('')
  }

  const handleKeyDown = (event) => {
    if (disabled) return
    const { value } = event.target
    if (value === '') {
      if (event.which === BACKSCAPE_KEY_CODE || event.which === DELETE_KEY_CODE) {
        onChange(values.slice(0, -1), name)
      }
      return
    }

    if (event.keyCode === ENTER_KEY_CODE) {
      event.persist()
      handleFormatValues(
        event,
        options
          ? options.filter((f) => f.label.toLowerCase().includes(value.toLowerCase()))[0].value
          : value
      )
    }
  }

  const handleInputBlur = (event) => {
    if (disabled) return
    handleFormatValues(event, value)
  }

  const handleRemoveValue = (value) => {
    if (disabled) return
    onChange(
      values.filter(
        (val) => val !== (options ? options.find((v) => v.label === value).value : value)
      ),
      name
    )
  }

  return (
    <div
      className={classNames('multi-value-input', className, {
        'multi-value-input--disabled': disabled,
      })}
    >
      <If condition={label}>
        <label className="multi-value-input__label">{label}</label>
      </If>
      <div
        className={classNames('multi-value-input__base', {
          'opacity-50 bg-gray-300': disabled,
        })}
      >
        <For each="chip" index="index" of={values}>
          <Chip
            className="multi-value-input__chip"
            key={index}
            onClick={handleRemoveValue}
            onButtonClick={typeof onSelect === 'function' ? () => onSelect(chip) : null}
            small
            value={
              options
                ? options.find((o) => o.value === chip).label
                : chip.name || chip.label || chip
            }
            selected={chip === selected}
          />
        </For>
        <If condition={options}>
          <Select
            className="multi-value-input__input"
            disabled={disabled}
            name={name}
            onChange={handleValueChange}
            onInputChange={handleInputChange}
            onBlur={handleInputBlur}
            customOnKeyDown={handleKeyDown}
            onPaste={handlePaste}
            value={value}
            inputValue={inputValue}
            options={options ? options.filter((o) => !values.includes(o.value)) : undefined}
            useReactSelect
            menuPosition={useFixedMenuPosition ? 'fixed' : undefined}
            styles={{
              // eslint-disable-next-line no-unused-vars
              menuPortal: ({ left, top, ...provided }) => ({
                ...provided,
              }),
            }}
          />
        </If>
        <If condition={!options}>
          <input
            className="multi-value-input__input"
            disabled={disabled}
            name={name}
            onChange={handleValueChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            value={value}
          />
        </If>
      </div>
    </div>
  )
}

MultiValueInput.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  values: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.string,
  pasteRegex: PropTypes.instanceOf(RegExp),
}

MultiValueInput.defaultProps = {
  className: '',
  label: '',
  name: '',
  onChange: () => {},
  onSelect: null,
  values: [],
  selected: '',
  pasteRegex: /\r?\n| /g,
}

export default React.memo(MultiValueInput)
